// CandidateEvidencePanel — slide-out panel showing full AI match evidence
import React from 'react';
import { X, Star, AlertCircle, CheckCircle, Award, Brain } from 'lucide-react';

const STAGE_STYLE = {
  'Offer Letter': 'bg-emerald-100 text-emerald-700',
  'Assessment':   'bg-violet-100 text-violet-700',
  'Resumes':      'bg-orange-100 text-orange-700',
  'JD':           'bg-indigo-100 text-indigo-700',
};

function ScoreBar({ label, value, color = '#f97316' }) {
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600 font-medium">{label}</span>
        <span className="font-bold" style={{ color }}>{value}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-2 rounded-full transition-all" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function CandidateEvidencePanel({ candidate, onClose }) {
  if (!candidate) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto flex flex-col">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-bold text-gray-900 text-lg">{candidate.candidateName}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{candidate.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Workflow stage + match score */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STAGE_STYLE[candidate.workflowStage] || 'bg-gray-100 text-gray-600'}`}>
              {candidate.workflowStage}
            </span>
            <span className="text-2xl font-extrabold text-orange-500">{candidate.matchScore}%</span>
            <span className="text-sm text-gray-400">match for {candidate.appliedJD}</span>
          </div>

          {/* Assessment scores */}
          {(candidate.scores?.preScreening > 0 || candidate.scores?.psychoanalytical > 0 || candidate.scores?.finalInterview > 0) && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Assessment Performance</h3>
              {candidate.scores.ats         > 0 && <ScoreBar label="ATS Score"          value={candidate.scores.ats}           color="#4338ca" />}
              {candidate.scores.preScreening > 0 && <ScoreBar label="Pre-Screening"      value={candidate.scores.preScreening}  color="#7c3aed" />}
              {candidate.scores.psychoanalytical > 0 && <ScoreBar label="Psychoanalytical" value={candidate.scores.psychoanalytical} color="#db2777" />}
              {candidate.scores.finalInterview > 0 && <ScoreBar label="Final Interview"   value={candidate.scores.finalInterview} color="#f97316" />}
            </div>
          )}

          {/* Applied JD evidence */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Star className="w-4 h-4 text-orange-400" /> Applied JD Match Evidence
            </h3>
            <p className="text-xs text-gray-400 mb-2">{candidate.appliedJD}</p>

            {candidate.strengths.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-emerald-600 mb-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Strengths</p>
                <ul className="space-y-1">
                  {candidate.strengths.map((s, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                      <span className="text-emerald-400 mt-0.5 shrink-0">•</span>{s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {candidate.concerns.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-red-500 mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Concerns</p>
                <ul className="space-y-1">
                  {candidate.concerns.map((c, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                      <span className="text-red-400 mt-0.5 shrink-0">•</span>{c}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {candidate.matchedKeywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {candidate.matchedKeywords.slice(0, 12).map((k, i) => (
                  <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full text-xs font-medium">{k}</span>
                ))}
              </div>
            )}

            {candidate.summary && typeof candidate.summary === 'string' && (
              <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-xl p-3 leading-relaxed">{candidate.summary}</p>
            )}

            {candidate.finalInterviewFeedback && (
              <div className="mt-3 p-3 bg-violet-50 rounded-xl">
                <p className="text-xs font-semibold text-violet-600 mb-1 flex items-center gap-1"><Brain className="w-3 h-3" /> Interview Insight</p>
                <p className="text-sm text-gray-700">{candidate.finalInterviewFeedback}</p>
              </div>
            )}
          </div>

          {/* Alternative JD recommendations */}
          {candidate.recommendations.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Award className="w-4 h-4 text-violet-500" /> Alternative Role Matches
              </h3>
              <div className="space-y-3">
                {candidate.recommendations.map((rec, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800 text-sm">{rec.jobTitle}</span>
                      <span className="text-lg font-extrabold text-violet-600">{rec.matchScore}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                      <div className="h-1.5 bg-violet-400 rounded-full" style={{ width: `${rec.matchScore}%` }} />
                    </div>
                    {rec.evidence.length > 0 && (
                      <ul className="space-y-1">
                        {rec.evidence.map((e, j) => (
                          <li key={j} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="text-violet-400 shrink-0 mt-0.5">•</span>{e}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {candidate.recommendations.length === 0 && (
            <div className="text-center py-4 text-gray-400 text-sm">
              No AI recommendations generated yet. Click "Generate" in the talent pool table.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
